/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { type TApplication } from '@/interfaces'
import { OfflineState } from '@/state/offline-management'

export const DB_VERSION = 1
export const APPLICATIONS_STORE = 'applications'
export const DELETED_APPLIES_STORE = 'deleteApplications'
export const USER_STORE = 'session'

const BD_STORES = [APPLICATIONS_STORE, DELETED_APPLIES_STORE, USER_STORE]

const USER_STORE_IDS = {
  application: 'application',
  convocatorias: 'convocatorias',
  formEntities: 'formEntities',
  refreshData: 'refresh',
  token: 'token',
  user: 'user',
}

export const openDB = async () => {
  const DB_NAME = OfflineState.DBName.peek()

  if (!DB_NAME) {
    return await Promise.reject(new Error('BD no existente'))
  }

  return await new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    // Se ejecuta cuando la base de datos se crea o se actualiza
    request.onupgradeneeded = (_) => {
      const db = request.result

      BD_STORES.forEach((store) => {
        if (!db.objectStoreNames.contains(store)) {
          db.createObjectStore(store, { keyPath: 'id' })
        }
      })
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = (_) => reject(request.error)
  })
}

// Función para guardar múltiples objetos en un store
export const saveMultipleToStore = async (
  storeName: string,
  applicationsData: TApplication[]
) => {
  const db = await openDB()
  // Filtra objetos vacíos o sin id antes de guardar
  const filteredData = applicationsData.filter(
    (item) => item && typeof item === 'object' && Object.keys(item).length > 0 && item.id && typeof item.id === 'string' && item.id.trim()
  );
  if (filteredData.length !== applicationsData.length) {
    console.warn('[IndexedDB] Se filtraron objetos vacíos o sin id antes de guardar:', applicationsData.filter(item => !item || typeof item !== 'object' || Object.keys(item).length === 0 || !item.id || typeof item.id !== 'string' || !item.id.trim()))
  }
  return await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const itemsStore = transaction.objectStore(storeName)

    let hasError = false
    filteredData.forEach((item, idx) => {
      try {
        itemsStore.put(item)
      } catch (e) {
        hasError = true
        console.error(`[IndexedDB] Error guardando el objeto en la posición ${idx}:`, item, e)
      }
    })

    transaction.oncomplete = () => {
      if (hasError) {
        reject(new Error('Uno o más objetos no se pudieron guardar. Revisa la consola para más detalles.'))
      } else {
        resolve()
      }
    }
    transaction.onerror = (_) => reject(transaction.error)
  })
}

// Función para obtener datos de un store específico
export const getFromStore = async (storeName: string, key: string) => {
  const db = await openDB()
  return await new Promise<TApplication>((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    const request = store.get(key)

    request.onsuccess = () => resolve(request.result as TApplication)
    request.onerror = (_) => reject(request.error)
  })
}

// Función para obtener todos los datos de un store
export const getAllFromStore = async (storeName: string) => {
  const db = await openDB()
  return await new Promise<TApplication[]>((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    const request = store.openCursor()
    const result: TApplication[] = []

    request.onsuccess = () => {
      const cursor = request.result
      if (cursor) {
        result.push(cursor.value as TApplication)
        cursor.continue()
      } else {
        resolve(result)
      }
    }

    request.onerror = (_) => reject(request.error)
  })
}

interface TDeleteFromStoreProps {
  key?: string
  keys?: string[]
  storeName: string
}
// Función para eliminar un objeto de un store
export const deleteFromStore = async ({
  key,
  keys,
  storeName,
}: TDeleteFromStoreProps) => {
  const db = await openDB()

  return await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)

    if (key) {
      store.delete(key)
    }

    if (keys) {
      keys.forEach((itemKey) => store.delete(itemKey))
    }

    transaction.oncomplete = () => resolve()
    transaction.onerror = (_) => reject(transaction.error)
  })
}

// Función para eliminar toda la base de datos
export const clearAllDB = async (): Promise<void> => {
  const DB_NAME = OfflineState.DBName.peek()

  if (!DB_NAME) {
    return await Promise.reject(new Error('BD no existente'))
  }

  return await new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME)

    request.onsuccess = () => {
      resolve()
    }

    request.onerror = (event) => {
      reject(event)
    }
  })
}

const offlineDB = {
  clearAllDB,
  deleteFromStore,
  getAllFromStore,
  getFromStore,
  openDB,
  saveMultipleToStore,
  stores: {
    APPLICATIONS_STORE,
    DELETED_APPLIES_STORE,
    USER_STORE,
  },
  userStoreIds: USER_STORE_IDS,
}

export default offlineDB
